import { useFadeInObserver } from '@/hooks/use-fade-in-observer';
import {
  Search, BarChart3, LineChart, Target, Bug, FileCheck,
  Award, Globe, Megaphone, Facebook, Palette, Database,
  type LucideIcon,
} from 'lucide-react';

const ToolCard = ({ name, Icon, delay }: { name: string; Icon: LucideIcon; delay: number }) => {
  const { ref, isVisible } = useFadeInObserver(delay);
  return (
    <div
      ref={ref}
      className={`group relative bg-card border border-border rounded-xl p-6 flex flex-col items-center justify-center gap-3 transition-all duration-500 hover:border-primary hover:shadow-[var(--shadow-elegant)] hover:-translate-y-1 cursor-default ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />
      <div className="relative w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center transition-all duration-300 group-hover:bg-primary group-hover:scale-110 group-hover:rotate-6">
        <Icon className="w-6 h-6 text-primary transition-colors duration-300 group-hover:text-primary-foreground" aria-hidden="true" />
      </div>
      <p className="relative text-sm font-medium text-center text-foreground/80 group-hover:text-foreground transition-colors">
        {name}
      </p>
    </div>
  );
};

const tools = [
  { name: 'Google Search Console', icon: Search },
  { name: 'Google Analytics', icon: BarChart3 },
  { name: 'Ahrefs', icon: LineChart },
  { name: 'SEMrush', icon: Target },
  { name: 'Moz Pro', icon: Award },
  { name: 'Screaming Frog', icon: Bug },
  { name: 'Yoast SEO', icon: FileCheck },
  { name: 'Rank Math', icon: Database },
  { name: 'WordPress', icon: Globe },
  { name: 'Google Ads', icon: Megaphone },
  { name: 'Facebook Ads', icon: Facebook },
  { name: 'Canva', icon: Palette },
];

const ToolsSection = () => {
  return (
    <section id="tools" className="py-24" aria-label="SEO tools and technologies">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-primary font-semibold mb-2 uppercase tracking-wider text-sm">Stack</p>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Tools & Technologies</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Industry-leading SEO and marketing tools I use daily to deliver measurable results.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-6xl mx-auto">
          {tools.map((tool, i) => (
            <ToolCard key={tool.name} name={tool.name} Icon={tool.icon} delay={i * 50} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ToolsSection;
